import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Sparkles, Globe2, CalendarDays, Video } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

function WordWall() {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  const words = Array.from({ length: 72 }, (_, i) => i);

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      data-testid="cpmc-hero-wordwall"
      className="relative h-full w-full"
      style={{ perspective: 900 }}
    >
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative flex h-full w-full items-stretch"
      >
        <div className="relative h-full w-[46%] overflow-hidden border-l-[10px] border-ink sm:border-l-[14px]">
          <div className="grid h-full grid-cols-2 content-between gap-x-1 py-1 pl-1 sm:grid-cols-3">
            {words.map((i) => (
              <span
                key={i}
                className="select-none truncate whitespace-nowrap font-mono text-[7px] font-medium uppercase leading-snug tracking-tight text-ink/70 sm:text-[10px] sm:tracking-normal"
              >
                {i === 34 ? (
                  <span className="font-bold text-blaze">possible</span>
                ) : (
                  "impossible"
                )}
              </span>
            ))}
          </div>
        </div>

        <div className="relative h-full flex-1">
          <svg viewBox="0 0 460 420" className="absolute inset-0 h-full w-full" aria-hidden="true">
            <path
              d="M 420 200 L 432 242 L 404 268 L 426 302 L 384 322 L 392 362 L 348 348 L 330 386 L 300 356 L 268 392 L 250 352 L 210 380 L 200 338 L 158 352 L 168 308 L 126 300 L 152 262 L 118 236 L 158 216 L 138 176 L 182 172 L 178 128 L 222 148 L 232 104 L 268 138 L 296 100 L 312 142 L 356 122 L 352 168 L 398 162 L 386 202 Z"
              fill="#F08A24"
            />
            <circle cx="415" cy="92" r="6" fill="#F08A24" />
            <circle cx="441" cy="142" r="4" fill="#F08A24" />
            <circle cx="100" cy="382" r="5" fill="#F08A24" />
            <circle cx="62" cy="198" r="4" fill="#F08A24" />
            <circle cx="392" cy="402" r="7" fill="#F08A24" />
            <circle cx="150" cy="58" r="5" fill="#F08A24" />
            <g stroke="#0A1128" strokeLinecap="round" fill="none">
              <path d="M253 112 L232 215" strokeWidth="36" />
              <path d="M248 130 L162 232" strokeWidth="20" />
              <path d="M250 128 L303 186" strokeWidth="20" />
              <path d="M232 215 L273 336" strokeWidth="26" />
              <path d="M232 215 L189 336" strokeWidth="26" />
              <path d="M118 280 L100 346" strokeWidth="30" />
              <path d="M112 292 L158 238" strokeWidth="18" />
              <path d="M100 346 L162 356" strokeWidth="24" />
              <path d="M100 346 L60 358" strokeWidth="24" />
            </g>
            <circle cx="257" cy="86" r="24" fill="#0A1128" />
            <circle cx="121" cy="256" r="21" fill="#0A1128" />
          </svg>
        </div>

        <motion.div
          style={{ transform: "translateZ(60px)" }}
          className="absolute -bottom-5 left-3 rounded-2xl border border-ink/10 bg-cream px-5 py-4 shadow-xl sm:-left-5"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">
            Cohorte d'octobre 2026
          </p>
          <p className="font-display text-lg font-bold">Places limitées</p>
        </motion.div>

        <motion.div
          style={{ transform: "translateZ(40px)" }}
          className="absolute right-3 top-6 rounded-full bg-sun px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink shadow-lg sm:-right-4"
        >
          25+ pays
        </motion.div>
      </motion.div>
    </div>
  );
}

export default function Hero() {
  const stats = [
    { icon: Globe2, label: "25+ pays", sub: "réseau mondial" },
    { icon: CalendarDays, label: "1er oct. 2026", sub: "début de cohorte" },
    { icon: Video, label: "100 % en ligne", sub: "19 semaines" },
  ];

  return (
    <section id="hero" className="relative overflow-hidden pt-28 sm:pt-32">
      <div className="pointer-events-none absolute -left-40 top-24 h-96 w-96 rounded-full bg-mint/50 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-sun/30 blur-3xl" />

      <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-20 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35, ease }}
            data-testid="cpmc-hero-promo-pill"
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/70 px-4 py-2 backdrop-blur"
          >
            <Sparkles className="h-3.5 w-3.5 text-blaze" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/70 sm:text-xs">
              Pat Deegan & Associates — En français
            </span>
          </motion.div>

          <h1
            data-testid="cpmc-hero-headline"
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            <Line delay={0.45}>Formation</Line>
            <Line delay={0.58}>
              <span className="text-outline">CPMC</span>
            </Line>
            <Line delay={0.71}>
              <span className="relative inline-block">
                <span className="relative z-10">En français.</span>
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 1.35, ease }}
                  className="absolute -bottom-1 left-0 z-0 h-3 w-full origin-left bg-sun/80"
                />
              </span>
            </Line>
          </h1>

          <motion.p
            data-testid="cpmc-hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg"
          >
            Devenez <strong className="text-ink">Coach certifié en Médecine Personnelle (CPMC)</strong> :
            la formation de Pat Deegan & Associates, offerte dans plus de 25 pays,
            est maintenant disponible au Québec en français — animée par des pairs aidants certifiés.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2, ease }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#inscription"
              data-testid="cpmc-hero-primary-cta"
              className="group flex items-center gap-2 rounded-full bg-ink px-7 py-4 font-semibold text-cream shadow-lg shadow-ink/20 transition-all duration-300 hover:bg-teal-dark hover:shadow-teal/30"
            >
              Réserver ma place
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#flyer"
              data-testid="cpmc-hero-btn-open-flyer"
              className="rounded-full border-2 border-ink px-7 py-[14px] font-semibold text-ink transition-colors duration-300 hover:bg-sun hover:border-sun"
            >
              Voir le flyer promo
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.45 }}
            className="mt-12 grid max-w-xl grid-cols-3 gap-4"
          >
            {stats.map((s) => (
              <div key={s.label} className="border-l-2 border-ink/15 pl-3">
                <s.icon className="mb-2 h-4 w-4 text-teal" />
                <p className="font-display text-sm font-bold sm:text-base">{s.label}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-ink/50">{s.sub}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.7, ease }}
          className="relative h-[420px] sm:h-[500px]"
        >
          <WordWall />
        </motion.div>
      </div>
    </section>
  );
}
